import { animated, useSpring } from '@react-spring/web';
import pluralize from 'pluralize';
import { type ComponentProps, memo, type ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { AutoSizer, List, type ListRowRenderer } from 'react-virtualized';

import { Button } from '@air/primitive-button';
import { cn } from '@air/utils-tailwind';

const MAX_UPLOAD_HEIGHT = 450;
const HEADER_ROW_HEIGHT = 38;
const ERROR_HEADER_ROW_HEIGHT = 42;
const HEADER_ROW_TOP_MARGIN = 6;
const UPLOAD_ROW_HEIGHT = 52;
const LIST_TOP_PADDING = 6;
const LIST_BOTTOM_PADDING = 10;

export type UploaderPaneListProps = {
  ongoingItems: ReactNode[];
  erroredItems: ReactNode[];
  abortedItems: ReactNode[];
  completedItems: ReactNode[];
  stoppedItems: ReactNode[];
  onRetryAll?: () => void;
  isUploaderOpen: boolean;
  maxUploadHeight?: number;
  className?: string;
};

const AbortedItemsHeader = memo(
  ({
    numberOfItems,
    onRetryAll,
    style,
  }: Pick<ComponentProps<'div'>, 'style'> & {
    numberOfItems: number;
    onRetryAll: UploaderPaneListProps['onRetryAll'];
  }) => (
    <div
      className="mr-4 mb-2 ml-2 flex items-center justify-between border-b ads:border-b-ads-gray-5 border-b-gray-5 py-2 pr-0 pl-2"
      style={style}
    >
      <div className="font-bold ads:text-metadata text-10 text-gray-9 uppercase tracking-wider">
        {pluralize('Cancelled', numberOfItems, true)}
      </div>

      {onRetryAll && (
        <Button appearance="ghost" color="gray" onClick={onRetryAll} size="small">
          Retry all
        </Button>
      )}
    </div>
  ),
);

AbortedItemsHeader.displayName = 'AbortedItemsHeader';

const ErroredItemsHeader = memo(
  ({
    numberOfItems,
    onRetryAll,
    style,
  }: Pick<ComponentProps<'div'>, 'style'> & {
    numberOfItems: number;
    onRetryAll: UploaderPaneListProps['onRetryAll'];
  }) => (
    <div
      className="mr-4 mb-1.5 ml-2 flex items-center justify-between border-b ads:border-b-ads-gray-5 border-b-gray-5 py-2 pr-0 pl-2"
      style={style}
    >
      <div className="font-bold ads:text-metadata text-10 text-gray-9 uppercase tracking-wider">
        {pluralize('Errors', numberOfItems, true)}
      </div>

      {onRetryAll && (
        <Button appearance="ghost" color="gray" onClick={onRetryAll} size="small">
          Retry all
        </Button>
      )}
    </div>
  ),
);

ErroredItemsHeader.displayName = 'ErroredItemsHeader';

const CompletedItemsHeader = memo(
  ({ numberOfItems, style }: Pick<ComponentProps<'div'>, 'style'> & { numberOfItems: number }) => (
    <div
      className="mb-2.5 flex h-7 items-center ads:bg-subtle bg-gray-3 px-4 font-bold ads:text-metadata text-10 text-gray-9 uppercase tracking-wider"
      style={style}
    >
      {numberOfItems} Completed
    </div>
  ),
);

CompletedItemsHeader.displayName = 'CompletedItemsHeader';

enum PaneListRowType {
  item,
  header,
  errorHeader,
}

type PaneListRow = {
  type: PaneListRowType;
  row: ReactNode;
};

export const FileStatusTrackingPanelList = memo(
  ({
    isUploaderOpen,
    ongoingItems,
    erroredItems,
    completedItems,
    abortedItems,
    stoppedItems,
    onRetryAll,
    maxUploadHeight = MAX_UPLOAD_HEIGHT,
    className,
  }: UploaderPaneListProps) => {
    const listRef = useRef<List>(null);

    const rows: PaneListRow[] = useMemo(() => {
      const itemsToRender: PaneListRow[] = [];

      if (stoppedItems.length) {
        itemsToRender.push(
          ...stoppedItems.map((item) => ({
            row: item,
            type: PaneListRowType.item,
          })),
        );
      }

      if (ongoingItems.length) {
        itemsToRender.push(
          ...ongoingItems.map((item) => ({
            row: item,
            type: PaneListRowType.item,
          })),
        );
      }

      if (erroredItems.length) {
        itemsToRender.push({
          row: (
            <ErroredItemsHeader
              numberOfItems={erroredItems.length}
              onRetryAll={onRetryAll}
              style={{ marginTop: itemsToRender.length > 0 ? HEADER_ROW_TOP_MARGIN : 0 }}
            />
          ),
          type: PaneListRowType.errorHeader,
        });
        itemsToRender.push(
          ...erroredItems.map((item) => ({
            row: item,
            type: PaneListRowType.item,
          })),
        );
      }

      if (completedItems.length) {
        itemsToRender.push({
          row: (
            <CompletedItemsHeader
              numberOfItems={completedItems.length}
              style={{ marginTop: itemsToRender.length > 0 ? HEADER_ROW_TOP_MARGIN : 0 }}
            />
          ),
          type: PaneListRowType.header,
        });
        itemsToRender.push(
          ...completedItems.map((item) => ({
            row: item,
            type: PaneListRowType.item,
          })),
        );
      }

      if (abortedItems.length) {
        itemsToRender.push({
          row: (
            <AbortedItemsHeader
              numberOfItems={abortedItems.length}
              onRetryAll={onRetryAll}
              style={{ marginTop: itemsToRender.length > 0 ? HEADER_ROW_TOP_MARGIN : 0 }}
            />
          ),
          type: PaneListRowType.errorHeader,
        });
        itemsToRender.push(
          ...abortedItems.map((item) => ({
            row: item,
            type: PaneListRowType.item,
          })),
        );
      }

      return itemsToRender;
    }, [completedItems, erroredItems, onRetryAll, ongoingItems, abortedItems, stoppedItems]);

    const rowRenderer: ListRowRenderer = useCallback(
      ({ index, style, key }) => {
        return (
          <li key={key} style={style}>
            {rows[index].row}
          </li>
        );
      },
      [rows],
    );

    const getRowHeight = useCallback(
      ({ index }: { index: number }) => {
        if (rows[index].type === PaneListRowType.header) {
          return index === 0 ? HEADER_ROW_HEIGHT : HEADER_ROW_HEIGHT + HEADER_ROW_TOP_MARGIN;
        } else if (rows[index].type === PaneListRowType.errorHeader) {
          return index === 0 ? ERROR_HEADER_ROW_HEIGHT : ERROR_HEADER_ROW_HEIGHT + HEADER_ROW_TOP_MARGIN;
        } else {
          return UPLOAD_ROW_HEIGHT;
        }
      },
      [rows],
    );

    const contentHeight = useMemo(() => {
      const allRowsHeight = rows.reduce((acc, _, index) => {
        acc += getRowHeight({ index });
        return acc;
      }, 0);

      return Math.min(maxUploadHeight, allRowsHeight);
    }, [getRowHeight, maxUploadHeight, rows]);

    const firstRowIsItem = rows[0]?.type === PaneListRowType.item;
    const listHeight = contentHeight + (firstRowIsItem ? LIST_TOP_PADDING : 0) + LIST_BOTTOM_PADDING;

    const [springs] = useSpring(
      () => ({
        height: isUploaderOpen ? listHeight : 0,
        opacity: isUploaderOpen ? 1 : 0,
      }),
      [isUploaderOpen, listHeight],
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <We only want to recompute the row heights when the items change>
    useEffect(() => {
      // biome-ignore lint/suspicious/noUnnecessaryConditions: listRef is optional until AutoSizer mounts the List
      listRef?.current?.recomputeRowHeights();
    }, [ongoingItems.length, erroredItems.length, completedItems.length]);

    return (
      <animated.div
        className={cn(
          'm-0 list-none overflow-y-auto overflow-x-hidden ads:border-default border-gray-4 border-t',
          className,
        )}
        style={{
          height: springs.height,
          maxHeight: listHeight,
          opacity: springs.opacity,
        }}
      >
        {isUploaderOpen ? (
          <>
            {firstRowIsItem && <div style={{ height: LIST_TOP_PADDING }} />}
            <AutoSizer disableHeight>
              {({ width }) => (
                <List
                  height={contentHeight}
                  ref={listRef}
                  rowCount={rows.length}
                  rowHeight={getRowHeight}
                  rowRenderer={rowRenderer}
                  width={width}
                />
              )}
            </AutoSizer>
            <div style={{ height: LIST_BOTTOM_PADDING }} />
          </>
        ) : null}
      </animated.div>
    );
  },
);

FileStatusTrackingPanelList.displayName = 'FileStatusTrackingPanelList';
